import type { RefObject } from "react"
import { useEffect } from "react"

/**
 * The two focus behaviors the feed shell needs:
 *
 * 1. Keep the container focused across slide changes so the scoped keyboard
 *    grammar works at first paint and after any carousel nav.
 * 2. Reclaim focus when the user clicks into an embedded iframe — focus
 *    moves into the embed's document and arrow keys would otherwise die
 *    until the user clicked outside. This 50ms reclaim IS the documented
 *    keyboard path back out of an embed: click in, focus returns to the
 *    container within a tick, Escape and j/k are live again.
 */
export function useFeedFocus(
  containerRef: RefObject<HTMLElement | null>,
  selectedIndex: number,
): void {
  useEffect(() => {
    containerRef.current?.focus({ preventScroll: true })
  }, [containerRef, selectedIndex])

  useEffect(() => {
    const reclaim = (target: Node | null) => {
      const container = containerRef.current
      if (!container) return
      if (
        target &&
        target instanceof HTMLIFrameElement &&
        container.contains(target)
      ) {
        window.setTimeout(() => {
          container.focus({ preventScroll: true })
        }, 50)
      }
    }
    // Tab into an embed fires focusin on the iframe element; a CLICK into a
    // cross-origin iframe never does — the parent document only sees the
    // window blur, with activeElement already moved onto the iframe. Both
    // entrances reclaim the same way.
    const onFocusIn = (event: FocusEvent) => reclaim(event.target as Node)
    const onWindowBlur = () => reclaim(document.activeElement)
    document.addEventListener("focusin", onFocusIn)
    window.addEventListener("blur", onWindowBlur)
    return () => {
      document.removeEventListener("focusin", onFocusIn)
      window.removeEventListener("blur", onWindowBlur)
    }
  }, [containerRef])
}
